/**
 * KsaRatesPanel - the GLOBAL KSA monthly exchange-rate table, opened from the
 * "Exchange rates" button on the KSA page.
 *
 * SAR, USD and AED are pegged (1 USD = 3.75 SAR, 1 AED = 1/3.6725 USD) and
 * never fetched. EUR, GBP, ZAR and INR are the European Central Bank's
 * reference rate on the first working day of the month, filled in by the
 * server's daily job or by "Refresh from ECB" here. A rate typed here is a
 * manual rate and a refresh never overwrites it; a bank rate typed on a
 * purchase beats both, for that purchase.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { useApiBase } from '../../context/DataScope';
import { fmtDate } from '../../utils/format';
import { INPUT, BTN_PRIMARY } from '../forms/formStyles';
import { ksaCall, notifyKsaCostsChanged } from './KsaCostsPanel';
import {
  CURRENCIES,
  CURRENCY_NAMES,
  PEGGED,
  fmtRateExact,
  fmtRateFull,
  lastMonths,
  monthLabel,
  num,
  parseRate,
  rateRow,
  rateSourceText,
} from './ksaMoney';

const GREEN = '#1B5E20';
const RED = '#B71C1C';
const JSON_HEADERS = { 'Content-Type': 'application/json' };
const TH = {
  textAlign: 'left',
  fontSize: 9,
  fontWeight: 800,
  color: '#999',
  letterSpacing: 1,
  textTransform: 'uppercase',
  padding: '6px 10px',
  borderBottom: '1px solid #EEE',
  whiteSpace: 'nowrap',
};
const TD = {
  fontSize: 12,
  color: '#222',
  padding: '8px 10px',
  borderBottom: '1px solid #F2F4EE',
  verticalAlign: 'middle',
  fontVariantNumeric: 'tabular-nums',
};
const SMALL_BTN = {
  background: '#fff',
  border: '1px solid #D8DED4',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 10.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const ERROR_BOX = {
  padding: '8px 12px',
  background: '#FFEBEE',
  border: '1px solid #FFCDD2',
  borderRadius: 8,
  color: RED,
  fontSize: 12,
  fontWeight: 600,
};

/** The table body: the rates for one month, always all 7 currencies in a fixed order. */
export function RatesTable({ rows, onEdit, edit, setEdit, onSave, saving, editError, onRevert }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {['Currency', '1 X = N SAR', '1 X = N USD', 'Source', ''].map((h, i) => (
            <th key={i} style={TH}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {CURRENCIES.map((c) => {
          const r = rateRow(rows, c) || { currency: c };
          const pegged = PEGGED.includes(c) || r.source === 'peg';
          const sar = num(r.sar_per_unit);
          const usd = num(r.usd_per_unit);
          const editing = edit && edit.currency === c;
          return (
            <tr key={c}>
              <td style={TD}>
                <b>{c}</b> <span style={{ color: '#888', fontSize: 11 }}>{CURRENCY_NAMES[c]}</span>
              </td>
              <td style={TD}>
                {editing ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    1 {c} =
                    <input
                      autoFocus
                      style={{ ...INPUT, width: 120, textAlign: 'right' }}
                      value={edit.text}
                      inputMode="decimal"
                      onChange={(e) => setEdit({ currency: c, text: e.target.value.replace(/[^0-9.,]/g, '') })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSave();
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                          e.preventDefault();
                          setEdit(null);
                        }
                      }}
                      aria-label={`SAR per 1 ${c}`}
                    />
                    SAR
                  </span>
                ) : sar ? (
                  `1 ${c} = ${fmtRateExact(sar)} SAR`
                ) : (
                  <span style={{ color: '#bbb' }}>no rate yet</span>
                )}
              </td>
              <td style={TD}>{usd ? `1 ${c} = ${fmtRateExact(usd)} USD` : <span style={{ color: '#bbb' }}>-</span>}</td>
              <td style={{ ...TD, color: r.source === 'manual' ? '#6A1B9A' : r.source ? '#557052' : '#bbb', fontWeight: 600 }}>
                {pegged ? 'Fixed peg' : rateSourceText(r, fmtDate)}
              </td>
              <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {pegged ? null : editing ? (
                  <>
                    <button
                      type="button"
                      style={{ ...SMALL_BTN, color: '#fff', background: GREEN, border: `1px solid ${GREEN}` }}
                      onClick={onSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>{' '}
                    <button type="button" style={SMALL_BTN} onClick={() => setEdit(null)}>
                      Cancel
                    </button>
                    {editError && (
                      <div
                        role="alert"
                        style={{ color: RED, fontSize: 11, fontWeight: 600, marginTop: 4, whiteSpace: 'normal', textAlign: 'left' }}
                      >
                        {editError}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {r.source === 'manual' && onRevert && (
                      <>
                        <button
                          type="button"
                          style={SMALL_BTN}
                          onClick={() => onRevert(c)}
                          disabled={!!edit || saving}
                          title="Drop the typed rate and use the ECB rate for this month"
                        >
                          Use ECB rate
                        </button>{' '}
                      </>
                    )}
                    <button type="button" style={SMALL_BTN} onClick={() => onEdit(c, sar)} disabled={!!edit}>
                      Edit
                    </button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function KsaRatesPanel({ onClose }) {
  const base = useApiBase();
  const months = useMemo(() => lastMonths(36), []); // far enough back to reach a quote month
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState(null); // { month, rows }
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [edit, setEdit] = useState(null); // { currency, text }
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // a slow answer for a month no longer picked must not land on the new one
  const shown = useRef(month);
  shown.current = month;
  const load = useCallback(async () => {
    setError('');
    try {
      const body = await ksaCall(`${base}/fx-rates?month=${month}`, undefined, `Could not load the ${monthLabel(month)} rates`);
      if (shown.current === month) setData({ month, rows: (body && body.rows) || [], can_refresh: body && body.can_refresh });
    } catch (e) {
      if (shown.current !== month) return;
      setData({ month, rows: [] });
      setError(e.message);
    }
  }, [base, month]);
  useEffect(() => {
    setData(null);
    setNote('');
    setEdit(null);
    load();
  }, [load]);

  const took = (body, m) => {
    notifyKsaCostsChanged('rates');
    if (shown.current !== m) return false; // the user moved to another month meanwhile
    if (body && Array.isArray(body.rows)) setData({ month: m, rows: body.rows, can_refresh: body.can_refresh });
    else load();
    return true;
  };

  const refresh = async () => {
    const m = month;
    setRefreshing(true);
    setError('');
    setNote('');
    try {
      const body = await ksaCall(
        `${base}/fx-rates/refresh`,
        { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ month }) },
        `Could not refresh the ${monthLabel(month)} rates from the ECB`
      );
      if (!took(body, m)) return;
      const r = (body && body.refreshed) || {};
      const day = r.rate_date || ((body && body.rows) || []).map((x) => (x.source === 'ECB' ? x.rate_date : null)).find(Boolean);
      const kept = Array.isArray(r.kept_manual) && r.kept_manual.length ? ` Manual rates kept: ${r.kept_manual.join(', ')}.` : '';
      const missing = Array.isArray(r.missing) && r.missing.length ? ` The ECB had no rate for ${r.missing.join(', ')}.` : '';
      setNote(
        r.fetched === false
          ? 'Already in the table: nothing to fetch.'
          : `${day ? `Updated from the ECB, rates of ${fmtDate(day)}.` : 'Refreshed from the ECB.'}${kept}${missing}`
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const saveEdit = async () => {
    if (!edit || saving) return;
    const v = parseRate(edit.text);
    if (v == null) {
      setEditError('Type how many SAR 1 unit is worth, a number above 0, with a dot for decimals (0.2288).');
      return;
    }
    // unchanged: do not turn an ECB rate into a manual one
    const was = num((rateRow((data && data.rows) || [], edit.currency) || {}).sar_per_unit);
    if (was != null && Math.abs(was - v) < 1e-10) {
      setEdit(null);
      return;
    }
    const m = month;
    setSaving(true);
    setEditError('');
    try {
      const body = await ksaCall(
        `${base}/fx-rates`,
        {
          method: 'PUT',
          headers: JSON_HEADERS,
          body: JSON.stringify({ month, currency: edit.currency, sar_per_unit: v }),
        },
        `Could not save the ${edit.currency} rate`
      );
      if (!took(body, m)) return;
      setNote(`${edit.currency} for ${monthLabel(month)} is now a manual rate. A refresh from the ECB will not change it.`);
      setEdit(null);
    } catch (e) {
      setEditError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const revert = async (c) => {
    const m = month;
    setSaving(true);
    setError('');
    setNote('');
    try {
      const body = await ksaCall(`${base}/fx-rates?month=${m}&currency=${c}`, { method: 'DELETE' }, `Could not drop the typed ${c} rate`);
      if (took(body, m))
        setNote(
          `${c} for ${monthLabel(m)} is back on the ECB rate${rateRow((body && body.rows) || [], c)?.sar_per_unit ? '' : ' (none yet for this month)'}.`
        );
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // the server says when the ECB history it reads does not reach this month
  const noRefresh = !!(data && data.can_refresh === false);
  return (
    <Modal title="Exchange rates" onClose={onClose} width={820}>
      <div style={{ fontFamily: 'inherit' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ fontSize: 11.5, fontWeight: 700, color: '#555', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Month
            <select style={{ ...INPUT, width: 140 }} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || noRefresh}
            title={
              noRefresh
                ? "The ECB refresh covers recent months only. Type this month's rates by hand."
                : "Fetch this month's rates from the European Central Bank. Manual rates are kept."
            }
            style={{
              ...BTN_PRIMARY,
              padding: '8px 14px',
              opacity: refreshing || noRefresh ? 0.6 : 1,
              cursor: refreshing ? 'wait' : noRefresh ? 'not-allowed' : 'pointer',
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh from ECB'}
          </button>
          {noRefresh && (
            <span style={{ fontSize: 11.5, color: '#92400E', fontWeight: 600 }}>Older months are manual only: type the rates below.</span>
          )}
          {note && <span style={{ fontSize: 11.5, color: GREEN, fontWeight: 600 }}>{note}</span>}
        </div>
        {error && (
          <div role="alert" style={{ ...ERROR_BOX, marginBottom: 10 }}>
            {error}
          </div>
        )}
        {!data || data.month !== month ? (
          <div style={{ fontSize: 11.5, color: '#aaa', padding: '12px 0' }}>Loading the {monthLabel(month)} rates...</div>
        ) : (
          <RatesTable
            rows={data.rows}
            edit={edit}
            setEdit={(e) => {
              setEdit(e);
              setEditError('');
            }}
            onEdit={(c, sar) => {
              setEdit({ currency: c, text: sar ? fmtRateFull(sar) : '' });
              setEditError('');
            }}
            onRevert={revert}
            onSave={saveEdit}
            saving={saving}
            editError={editError}
          />
        )}
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            background: '#FAFBF7',
            border: '1px solid #ECEFE6',
            borderRadius: 8,
            fontSize: 11.5,
            color: '#555',
            lineHeight: 1.6,
          }}
        >
          SAR, USD and AED are pegged.
          <br />
          Other rates are the European Central Bank rate on the first working day of the month. A bank rate typed on a purchase always wins.
        </div>
      </div>
    </Modal>
  );
}
